import { type ReactNode, createContext, use, useMemo } from "react";
import type { ClaimStrategyResponse } from "@patent-studio/api-types";
import { type Lanes, SortableLanes } from "@/components/ui/SortableLanes";
import {
  useAddClaim,
  useReorderClaims,
  whenWritten,
} from "@/hooks/use-claim-strategy";
import { Refusal } from "./ChainControls";
import { useClaimSetMoments } from "./claim-set-moments";
import {
  INDEPENDENTS,
  carriedWords,
  claimLanes,
  orderWrites,
  placementOf,
} from "./claim-lanes";
import { latestRefusal } from "./latest-refusal";
import { announcePlaced } from "./PositionOptions";

const ArrangedContext = createContext<Lanes | null>(null);

/**
 * The set's order as the drag is showing it, which runs ahead of the server's
 * while a drag is on; the served order where nothing is being dragged.
 */
export function useArranged(stage: ClaimStrategyResponse): Lanes {
  return use(ArrangedContext) ?? claimLanes(stage);
}

/** How a reader hears each thing a drag of the set is about: the set, or a claim's or a position's number. */
function namer(stage: ClaimStrategyResponse) {
  const claims = new Map(
    stage.claims.map((c) => [c.claim_id, `claim ${String(c.number)}`]),
  );
  const positions = new Map(
    stage.unplaced.map((p) => [p.position_id, `position ${String(p.number)}`]),
  );
  return (id: unknown): string => {
    const key = String(id);
    const [kind, rest] = key.includes(":")
      ? [key.slice(0, key.indexOf(":")), key.slice(key.indexOf(":") + 1)]
      : ["", key];
    if (key === INDEPENDENTS || kind === "into") return "the claim set";
    if (kind === "position") return positions.get(rest) ?? "a position";
    if (kind === "onto" || kind === "under")
      return claims.get(rest) ?? "a claim";
    return claims.get(key) ?? "an item";
  };
}

/**
 * The claim set as something the attorney can rearrange by hand: forms,
 * independent claims and dependents reordered among their own, each finished
 * drag written as the new order, and a position waiting to be placed dragged
 * onto the claim or the form it should join.
 */
export function ClaimSetDrag({
  taskId,
  stage,
  children,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
  children: ReactNode;
}) {
  const served = useMemo(() => claimLanes(stage), [stage]);
  const claims = useReorderClaims(taskId);
  const add = useAddClaim(taskId);
  const { refused, starting } = latestRefusal([claims, add]);
  const { markTagging } = useClaimSetMoments();
  return (
    <SortableLanes
      served={served}
      name={namer(stage)}
      lands={["position"]}
      carried={(id) => carriedWords(stage, id)}
      onArrange={(arranged, putBack) => {
        for (const write of orderWrites(served, arranged)) {
          starting(claims, () => {
            claims.mutate(write, { onError: putBack });
          });
        }
      }}
      onLand={(landing) => {
        const request = placementOf(landing, stage);
        if (request === null) return;
        starting(add, () => {
          whenWritten(add.mutateAsync(request), (after) => {
            announcePlaced(stage, after, request, markTagging);
          });
        });
      }}
    >
      {({ lanes }) => (
        <ArrangedContext value={lanes}>
          {refused !== undefined && <Refusal error={refused.error} />}
          {children}
        </ArrangedContext>
      )}
    </SortableLanes>
  );
}
